import { DisplayError } from '@cotera/client/app/components/app/error';
import {
  Center,
  Icon,
  Loading,
  Text,
  Tooltip,
} from '@cotera/client/app/components/ui';
import { useAnalytics } from '@cotera/client/app/etc';
import { useStorage } from '@cotera/client/app/etc/storage/provider';
import { CacheResult } from '@cotera/client/app/etc/data/artifacts';
import {
  ModelScope,
  PersistentWatchable,
  ReadonlyWatchable,
  useAction,
  useActionsContext,
  useInScopeModel,
  useStatefullyAwareInScopeModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { Relation, Ty } from '@cotera/era';
import { DG } from '@cotera/sdk/core';
import { Assert, using } from '@cotera/utilities';
import { unionBy } from 'lodash';
import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DisposableTargetedModelContext } from '../actions/etc';
import { LlmRunColumnAction } from '../columns/llm/actions/run-llm-column.action';
import { ToolCallRunColumnAction } from '../columns/tool-call/actions/run-tool-call-column.action';
import {
  CellComponentProps,
  Column,
  DataGrid,
  DataGridBody,
  DataGridTopBarContainer,
  DefaultRowNumberHeader,
  HeaderComponentProps,
} from '../components/data-grid';
import { Button } from '../components/ui/button';
import { GraphModel } from '../graph/graph.model';
import { useElementHeight } from '../hooks/use-element-height';
import { makeColumnModel } from '../lib/make-column-model';
import { useToolSchemaLookup } from '@cotera/client/app/hooks/use-tool-schema';
import { ClearAllFiltersAction } from './actions/clear-all-filters.actions';
import { DownloadCsvAction } from './actions/download-csv.action';
import { OpenDetailViewAction } from './actions/open-details.action';
import { OpenSettingsAction } from './actions/open-settings.action';
import { ArtifactLoader } from './artifact-loader';
import { AtcDetailViewCellComponent, PreviewAtcCellComponent } from './atc';
import { BaseCellRenderer } from './cell';
import { DetailViewCell as DefaultDetailViewCell } from './detail-view';
import { MemoedHeaderRenderer } from './header';
import { SkeletonDataGrid, SkeletonDataGridBody } from './loading';
import { DataGridModel } from './model';
import { CellComponentPropsWithModel, DataGridColumnConfig } from './types';
import { usePaginatedDuckDbQuery } from './use-paginated-duckdb-query';
import { columnsFromRel, displayTyWrapper, iconForColumn } from './utils';
import { PkUniquenessWarning } from './pk-uniqueness-warning';

type DetailViewCellComponent = React.ForwardRefExoticComponent<
  {
    column: string;
    value: any;
    rel: Relation;
    focused: boolean;
  } & React.RefAttributes<HTMLDivElement>
>;

const CustomRowNumberHeaderRenderer = ({
  column,
  headerHeight,
  model,
}: {
  column: Column & { x: number; width: number };
  headerHeight: number;
  model: DataGridModel;
}) => {
  const headersExpanded = useWatchableValue(model.headersExpanded);
  const analytics = useAnalytics();

  return (
    <DefaultRowNumberHeader column={column} headerHeight={headerHeight}>
      <div className="flex flex-col gap-2 items-center mt-2.5">
        <Tooltip side="top" tooltipContent="Show column stats">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              const willExpand = !headersExpanded;
              analytics.track(
                willExpand ? 'Show Column Stats' : 'Close Column Stats',
                {
                  action: willExpand ? 'opened' : 'closed',
                }
              );
              model.headersExpanded.set(willExpand);
            }}
          >
            <Icon icon={headersExpanded ? 'chevron-down' : 'chevron-right'} />
          </Button>
        </Tooltip>
      </div>
    </DefaultRowNumberHeader>
  );
};

const RelationDataGrid: React.FC<{
  height: number | null;
  model: DataGridModel;
  fallbackBody: React.ReactNode;
  actions?: React.ReactNode;
  cacheKey: string;
  HeaderComponent?: React.ComponentType<
    HeaderComponentProps & {
      model: DataGridModel;
      rel: Relation;
    }
  >;
}> = ({ height, model, fallbackBody, actions, cacheKey, HeaderComponent }) => {
  const openDetailViewAction = useAction(OpenDetailViewAction);
  const actionsContext = useActionsContext();

  const HeaderRenderer = useMemo(() => {
    return (props: HeaderComponentProps) =>
      HeaderComponent ? (
        <HeaderComponent {...props} model={model} rel={model.rel} />
      ) : (
        <MemoedHeaderRenderer {...props} artifact={model.rel} model={model} />
      );
  }, [model, HeaderComponent]);

  const CellRenderer = useMemo(() => {
    return (props: CellComponentProps<any>) => {
      const CellRenderer = model.renderer?.cell ?? BaseCellRenderer;
      return <CellRenderer {...props} model={model} rel={model.rel} />;
    };
  }, [model]);

  const RowNumberHeaderRenderer = useMemo(() => {
    return (props: {
      column: Column & { x: number; width: number };
      headerHeight: number;
    }) => <CustomRowNumberHeaderRenderer {...props} model={model} />;
  }, [model]);

  return (
    <div
      className="flex flex-col gap-4"
      style={{
        height: height ?? '100%',
      }}
    >
      <DataGrid
        cacheKey={cacheKey}
        model={model}
        CellComponent={CellRenderer}
        HeaderComponent={HeaderRenderer}
        RowNumberHeaderComponent={RowNumberHeaderRenderer}
        rowHeight={36}
        headerHeight={model.headerHeight}
        maxColumnWidth={400}
        onSelectCell={async (row, col) => {
          await openDetailViewAction.execute({
            row,
            col,
          });
        }}
        body={
          <ErrorBoundary
            fallbackRender={({ error }) => <DisplayError error={error} />}
          >
            <Suspense fallback={fallbackBody}>
              <DataGridBodyRenderer model={model} CellRenderer={CellRenderer} />
            </Suspense>
          </ErrorBoundary>
        }
        topBar={() => <TopBar model={model} actions={actions} />}
        onFocus={() => {
          actionsContext.targetModel(model);
        }}
        onBlur={() => {
          //dont untarget the model as we keep it targetted, until the user clicks on
          // another datagrid or leaves the page
        }}
      />
    </div>
  );
};

const TopBar = ({
  model,
  actions,
}: {
  model: {
    totalLoadedRows: ReadonlyWatchable<number>;
    totalRows: ReadonlyWatchable<number | null>;
  };
  actions?: React.ReactNode;
}) => {
  const totalLoadedRows = useWatchableValue(model.totalLoadedRows);
  const totalRows = useWatchableValue(model.totalRows);

  return (
    <DataGridTopBarContainer className="flex h-12 justify-between">
      <div className="flex items-center gap-2">
        {totalRows !== null && (
          <Text.Caption>
            {`Loaded ${totalLoadedRows} of ${totalRows} rows`}
          </Text.Caption>
        )}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </DataGridTopBarContainer>
  );
};

const DataGridBodyRenderer = ({
  model,
  CellRenderer,
}: {
  model: DataGridModel;
  CellRenderer: React.ComponentType<CellComponentProps<any>>;
}) => {
  const dataRel = useWatchableValue(model.dataRel);

  const { data, loadMore, hasMore } = usePaginatedDuckDbQuery({
    rel: dataRel,
    take: 50,
    model,
  });

  return (
    <DataGridBody
      hasMore={hasMore}
      onLoadMore={loadMore}
      data={data}
      CellComponent={CellRenderer}
    />
  );
};

const ManagedDataGrid: React.FC<{
  height: number | null;
  rel: Relation;
  modelFactory: (artifact: CacheResult) => DataGridModel;
  actions?: React.ReactNode;
  columns: Watchable<Column[]>;
  headerComponent?: React.ComponentType<
    HeaderComponentProps & {
      model: DataGridModel;
      rel: Relation;
    }
  >;
  children?: React.ReactNode;
}> = ({
  height,
  rel,
  modelFactory,
  actions,
  columns,
  headerComponent,
  children,
}) => {
  const cacheKey = rel.eraqlHash();
  const fallbackBody = <SkeletonDataGridBody columns={columns} />;

  return (
    <Suspense
      fallback={<SkeletonDataGrid children={fallbackBody} columns={columns} />}
    >
      {height !== null && (
        <ArtifactLoader
          rel={rel}
          modelFactory={modelFactory}
          cacheKey={cacheKey}
        >
          {(model) => (
            <>
              <ModelScope models={[model]} />
              <RelationDataGrid
                actions={actions}
                model={model}
                height={height}
                fallbackBody={fallbackBody}
                cacheKey={cacheKey}
                HeaderComponent={headerComponent}
              />
              {children}
            </>
          )}
        </ArtifactLoader>
      )}
    </Suspense>
  );
};

const DefaultAtcCellComponent = (props: CellComponentPropsWithModel) => {
  return (
    <PreviewAtcCellComponent
      {...props}
      atcColumns={[]}
      getEditorState={() => {
        throw new Error('getEditorState not implemented');
      }}
      relName={''}
      llmConfig={{
        model: '',
        schedule: 'never',
        temperature: 0,
        maxOutputTokens: 0,
      }}
    />
  );
};

const UnmemoedGraphDataGrid: React.FC<{
  rel: Relation;
  relName: string;
  shouldShow: boolean;
  buildExplorer?: () => DG.BuildExplorer;
  CellComponent?: React.ComponentType<
    CellComponentProps<any> & {
      model: DataGridModel;
      rel: Relation;
    }
  >;
  AtcCellComponent?: React.ComponentType<
    CellComponentPropsWithModel & {
      relName: string;
      cellRenderer?: React.ComponentType<CellComponentPropsWithModel>;
    }
  >;
  DetailViewCell?: DetailViewCellComponent;
  actions?: React.ReactNode;
  managedProps?: {
    isTargeted?: boolean;
  };
  columnConfigOverride?: (column: Column) => Partial<Column>;
  headerPlayButton?: ((rel: Relation) => React.ReactNode) | false;
  storageKey?: string;
  showPkUniquenessWarning?: boolean;
}> = ({
  rel,
  relName,
  shouldShow,
  buildExplorer,
  CellComponent,
  AtcCellComponent: CustomAtcCellComponent,
  DetailViewCell,
  actions,
  managedProps,
  columnConfigOverride,
  headerPlayButton,
  storageKey = relName,
  showPkUniquenessWarning = true,
}) => {
  const graphModel = useInScopeModel(GraphModel);
  const buildExplorerFn = useCallback(() => {
    if (buildExplorer !== undefined) {
      return buildExplorer();
    }
    return graphModel.graph.snapshot();
  }, [graphModel, buildExplorer]);

  const AtcCellRenderer = useMemo(() => {
    const AtcWrapper = CustomAtcCellComponent ?? DefaultAtcCellComponent;
    return (props: CellComponentPropsWithModel) => {
      return (
        <AtcWrapper
          {...props}
          relName={relName}
          cellRenderer={
            CellComponent ??
            (BaseCellRenderer as React.ComponentType<CellComponentPropsWithModel>)
          }
        />
      );
    };
  }, [CellComponent, relName, CustomAtcCellComponent]);

  const HeaderComponent = useMemo(() => {
    return (
      props: HeaderComponentProps & {
        model: DataGridModel;
        rel: Relation;
      }
    ) => {
      const getQuickActions = () => {
        // If headerPlayButton is explicitly false, hide the button
        if (headerPlayButton === false) {
          return undefined;
        }
        // If headerPlayButton is provided, use it as the quick action
        if (headerPlayButton !== undefined) {
          return headerPlayButton(rel);
        }
        // Default behavior: show RunOnAllRowsAction for llm/workflow columns
        if (props.column.t === 'llm' || props.column.t === 'workflow') {
          return <RunOnAllRowsAction relName={relName} column={props.column} />;
        }
        return undefined;
      };

      return (
        <MemoedHeaderRenderer
          {...props}
          artifact={props.rel}
          model={props.model}
          quickActions={getQuickActions()}
        />
      );
    };
  }, [relName, headerPlayButton, rel]);

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => <DisplayError error={error} />}
    >
      <Suspense
        fallback={
          <Center className="h-full w-full">
            <Loading.Dots />
          </Center>
        }
      >
        {shouldShow && (
          <InnerGraphDataGrid
            showPkUniquenessWarning={showPkUniquenessWarning}
            rel={rel}
            relName={relName}
            storageKey={storageKey}
            buildExplorer={buildExplorerFn}
            CellComponent={CellComponent ?? AtcCellRenderer}
            HeaderComponent={HeaderComponent}
            DetailViewCell={DetailViewCell}
            actions={actions}
            managedProps={managedProps}
            columnConfigOverride={columnConfigOverride}
          />
        )}
      </Suspense>
    </ErrorBoundary>
  );
};

export const GraphDataGrid = React.memo(UnmemoedGraphDataGrid, (prev, next) => {
  return (
    prev.rel.eraqlHash() === next.rel.eraqlHash() &&
    prev.shouldShow === next.shouldShow
  );
});

const InnerGraphDataGrid: React.FC<{
  rel: Relation;
  relName: string;
  buildExplorer: () => DG.BuildExplorer;
  storageKey: string;
  HeaderComponent?: React.ComponentType<
    HeaderComponentProps & {
      model: DataGridModel;
      rel: Relation;
    }
  >;
  CellComponent?: React.ComponentType<
    CellComponentProps<any> & {
      model: DataGridModel;
      rel: Relation;
    }
  >;
  DetailViewCell?: DetailViewCellComponent;
  actions?: React.ReactNode;
  managedProps?: {
    isTargeted?: boolean;
  };
  columnConfigOverride?: (column: Column) => Partial<Column>;
  showPkUniquenessWarning?: boolean;
}> = ({
  rel: baseRel,
  relName,
  storageKey,
  buildExplorer,
  CellComponent,
  DetailViewCell,
  actions,
  managedProps,
  HeaderComponent,
  columnConfigOverride,
  showPkUniquenessWarning = true,
}) => {
  const [ref, height] = useElementHeight();
  const storage = useStorage();

  const getColumnConfigFn = useCallback(
    (key: string) => {
      return getColumnConfigType(buildExplorer(), relName)(key);
    },
    [buildExplorer, relName]
  );

  const columns = useMemo(() => {
    const relCols = columnsFromRel(baseRel, getColumnConfigFn);
    return PersistentWatchable.fromStorage<Column[]>({
      storage,
      storageKey: `${storageKey}-columns`,
      defaultValue: relCols,
      map: (x) => {
        //we union so that we show coluns that have not yet been stored
        //which can happen when the shape of the rel is different from the shape of the rel when it was stored
        const merged = unionBy(
          //only columns that exist in the rel should be taken into account
          //this handles the case where we have stored config against columns that not longer exist
          x
            .filter((c) => relCols.some((rc) => rc.key === c.key))
            .map((c, index) => {
              const { icon: _icon, ...rest } = c; // Remove icon from stored data
              const freshColumn = relCols.find((rc) => rc.key === c.key);
              return {
                ...rest,
                icon: freshColumn?.icon ?? 'text', // Use fresh icon from relCols
                inView: index < 8,
              };
            }), //show first 8 by default
          relCols,
          (c) => c.key
        );

        // Apply column config override if provided
        if (columnConfigOverride) {
          return merged.map((col) => ({
            ...col,
            ...columnConfigOverride(col),
          }));
        }

        return merged;
      },
    });
    //we explicitly ignore columnConfigOverride because we only want the first value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRel, getColumnConfigFn, storage, storageKey]);

  const DetailViewCellRenderer = useMemo(() => {
    return React.forwardRef<
      HTMLDivElement,
      {
        column: string;
        value: any;
        rel: Relation;
        focused: boolean;
        rowData?: Record<string, any>;
        rowIndex?: number;
      }
    >((props, ref) => {
      return (
        <AtcDetailViewCellComponent
          relName={relName}
          ref={ref}
          {...props}
          detailViewCellRenderer={
            DetailViewCell ??
            (DefaultDetailViewCell as React.ForwardRefExoticComponent<
              {
                column: string;
                value: any;
                rel: Relation;
                focused: boolean;
                rowData?: Record<string, any>;
                rowIndex?: number;
              } & React.RefAttributes<HTMLDivElement>
            >)
          }
          rowData={props.rowData ?? {}}
          rowIndex={props.rowIndex ?? 0}
        />
      );
    });
  }, [DetailViewCell, relName]);

  return (
    <div className="h-full w-full" ref={ref}>
      {showPkUniquenessWarning && (
        <PkUniquenessWarning relName={relName} baseRel={baseRel} />
      )}
      <ManagedDataGrid
        rel={baseRel}
        height={height}
        columns={columns}
        headerComponent={HeaderComponent}
        modelFactory={(artifact) => {
          const unwrapped = Assert.assertOk(artifact);
          const rel = Relation.wrap(unwrapped.ir);
          return new DataGridModel({
            rel,
            baseRel,
            id: storageKey,
            storage,
            columns,
            artifactMeta: unwrapped.meta ?? null,
            renderer: {
              cell: CellComponent,
              detailViewCell: DetailViewCellRenderer,
            },
            managedProps,
          });
        }}
        actions={
          <div className="flex items-center gap-2" key={relName}>
            <OpenDetailViewButton modelId={relName} />
            <ClearFiltersButton modelId={relName} />
            <OpenSettingsButton />
            <DownloadCsvButton />
            {actions}
          </div>
        }
      >
        <ColumnReconciler relName={relName} />
      </ManagedDataGrid>
    </div>
  );
};

const DownloadCsvButton: React.FC = () => {
  const downloadCsv = useAction(DownloadCsvAction);

  return (
    <Tooltip side="top" tooltipContent="Download CSV">
      <Button
        size="icon-sm"
        variant={'ghost'}
        onClick={async () => await downloadCsv.execute()}
      >
        <Icon icon="download" />
      </Button>
    </Tooltip>
  );
};

const ClearFiltersButton: React.FC<{
  modelId: string;
}> = ({ modelId }) => {
  const model = useStatefullyAwareInScopeModel(DataGridModel, modelId);

  if (model === null) {
    return null;
  }

  return <ClearFiltersButtonInner model={model} />;
};

const ClearFiltersButtonInner: React.FC<{
  model: DataGridModel;
}> = ({ model }) => {
  const clearFilters = useAction(ClearAllFiltersAction);
  const filters = useWatchableValue(model.filters);

  return (
    <Tooltip side="top" tooltipContent="Clear Filters">
      <Button
        disabled={filters.length === 0}
        size="icon-sm"
        variant={'ghost'}
        onClick={async () => await clearFilters.execute()}
      >
        <Icon icon="clear-filters" />
      </Button>
    </Tooltip>
  );
};

const OpenDetailViewButton: React.FC<{
  modelId: string;
}> = ({ modelId }) => {
  const dataset = useStatefullyAwareInScopeModel(DataGridModel, modelId);

  if (dataset === null) {
    return null;
  }

  return <OpenDetailViewButtonInner model={dataset} />;
};

const OpenDetailViewButtonInner: React.FC<{
  model: DataGridModel;
}> = ({ model }) => {
  const focusedCell = useWatchableValue(model.focusedCell);
  const openDetailView = useAction(OpenDetailViewAction);

  return (
    <Tooltip side="top" tooltipContent="Open Detail View">
      <Button
        disabled={focusedCell === null}
        size="icon-sm"
        variant={'ghost'}
        onClick={async () => await openDetailView.execute(focusedCell!)}
      >
        <Icon icon="document-text" />
      </Button>
    </Tooltip>
  );
};

const OpenSettingsButton: React.FC = () => {
  const openSettings = useAction(OpenSettingsAction);
  const analytics = useAnalytics();

  return (
    <Tooltip side="top" tooltipContent="Open Settings">
      <Button
        size="icon-sm"
        variant={'ghost'}
        onClick={async () => {
          analytics.track('Data Grid More Menu Options Opened', {
            source: 'settings_button',
          });
          await openSettings.execute();
        }}
      >
        <Icon icon="settings" />
      </Button>
    </Tooltip>
  );
};

const getColumnConfigType = (
  buildExplorer: DG.BuildExplorer,
  relName: string
) => {
  return (key: string): DataGridColumnConfig['t'] => {
    const config =
      buildExplorer.getColumn({
        rel: relName,
        name: key,
      })?.config ?? null;

    if (config === null) {
      return 'assumption';
    }

    return config.t;
  };
};

const RunOnAllRowsAction: React.FC<{ relName: string; column: Column }> = ({
  relName,
  column,
}) => {
  Assert.assert(column.t === 'llm' || column.t === 'workflow');
  const context = useActionsContext();
  const graphModel = useInScopeModel(GraphModel)!;
  const toolSchemaLookup = useToolSchemaLookup();

  const llmRun = useAction(LlmRunColumnAction);
  const toolCallRun = useAction(ToolCallRunColumnAction);

  return (
    <Button
      size={'sm'}
      variant={'outline'}
      className="p-1! w-fit! h-fit!"
      onClick={async () => {
        const ctx = new DisposableTargetedModelContext(
          context,
          makeColumnModel(
            graphModel,
            {
              parentDatasetName: relName,
              name: column.key,
              originalNode: {
                def: {
                  t: column.t as 'llm' | 'workflow',
                },
              },
            },
            toolSchemaLookup
          )
        );

        return await using(ctx, async () => {
          if (column.t === 'llm') {
            await llmRun.execute();
          } else if (column.t === 'workflow') {
            await toolCallRun.execute();
          }
        });
      }}
    >
      <Icon icon="play" />
    </Button>
  );
};

const useColumnReconciler = (relName: string) => {
  const graphModel = useInScopeModel(GraphModel)!;
  const dataGridModel = useInScopeModel(DataGridModel, relName);

  const graph = useWatchableValue(graphModel.graph);

  useEffect(() => {
    const datasetConfig = graph.getRel({
      symbol: relName,
    });
    if (!datasetConfig?.builtNode.isOk()) {
      return;
    }

    if (datasetConfig.builtNode.value.item.t !== 'rel') {
      return;
    }

    if (dataGridModel === null) {
      return;
    }

    const datasetColumns = datasetConfig.builtNode.value.item.cols;

    const newColumns = Object.entries(datasetColumns).filter(
      ([key, _value]) =>
        !dataGridModel.columns.snapshot().some((c) => c.key === key)
    );
    //remove columns that are not in the dataset
    //add columns that are in the dataset but not in the data grid
    const removedColumns =
      dataGridModel.columns.snapshot().filter((c) => {
        if (c.t === 'assumption') {
          return true;
        }
        return Object.entries(datasetColumns).some(
          ([key, _value]) => key === c.key
        );
      }) ?? [];

    const nextColumns = [
      ...removedColumns,
      ...newColumns.map(([key, _]): Column => {
        const graphColumn = graph.getColumn({
          rel: relName,
          name: key,
        });

        // Use fallback values if graph.getColumn() fails (e.g., during graph compilation)
        // This can happen when "run from start" triggers graph updates
        const columnType = graphColumn?.config?.t ?? 'assumption';
        const columnDataType = graphColumn?.ty ?? null;
        const fallbackType = Ty.ty('string');

        return {
          key,
          title: key,
          type: displayTyWrapper(columnType, columnDataType ?? fallbackType),
          icon: iconForColumn(columnType, columnDataType ?? fallbackType),
          t: columnType,
          status: 'visible',
          inView: true,
        };
      }),
    ];

    dataGridModel.columns.set(nextColumns);
  }, [dataGridModel, graph, relName]);
};

const ColumnReconciler = ({ relName }: { relName: string }) => {
  useColumnReconciler(relName);
  return null;
};
